import {
  Users,
  UserCheck,
  Wallet,
  AlertCircle,
  Plus,
  ClipboardCheck,
  FileText,
  Activity,
  TrendingUp,
  Currency,
  IndianRupee,
  PieChart,
  BarChart2,
  GraduationCap,
  Award,
  BookMarked,
  ArrowUpRight,
  CheckCircle,
  Clock,
  ArrowDownCircle,
  ArrowUpCircle,
  Calendar as CalendarIcon
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { getDashboardOverview, getFinanceDashboard } from "../../services/dashboardService";
import { useUser } from "../../context/AuthContext";
import DashboardFilters from "./DashboardFilters";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';

import "./DashboardHome.css";

const DashboardHome = () => {
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isParent = user.roleName === "PARENT";

  const initialLoadDone = useRef(false);
  const navigate = useNavigate();

  const today = new Date();

  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const [filters, setFilters] = useState({
    GroupId: "",
    BranchId: "",
    CourseId: "",
    Month: today.getMonth() + 1,
    Year: today.getFullYear(),
    fromDate: oneMonthAgo.toISOString().split("T")[0],
    toDate: today.toISOString().split("T")[0],
  });

  const [stats, setStats] = useState({
    totalInvoiceAmount: 0,
    totalFeesCollected: 0,
    totalFeesYetToCollect: 0,
    activeStudents: 1248,
    batchOccupancy: 85,
  });

    const [financeData, setFinanceData] = useState({
        summary: {},
        trend: [],
        recent: [],
        pendingApprovals: []
    });

  useEffect(() => {
    // Initial load on component mount
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      applyFilters();
    }
  }, []);

  const applyFilters = async () => {
    try {
      setLoading(true);
      const res = await getFinanceDashboard({
        fromDate: filters.fromDate,
        toDate: filters.toDate
      });

      const actualData = res.data || {};

      // 🔹 CONVERT TREND DATA TO NUMBERS FOR RECHARTS
      const formattedTrend = (actualData.trend || []).map(item => ({
        ...item,
        TotalGenerated: parseFloat(item.TotalGenerated || 0),
        TotalCollected: parseFloat(item.TotalCollected || 0)
      }));

      setFinanceData({
        summary: actualData.summary?.[0] || {},
        trend: formattedTrend,
        recent: actualData.recent || [],
        pendingApprovals: actualData.pendingApproval || actualData.pendingApprovals || []
      });
    } catch (err) {
      console.error("Dashboard load failed", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(val || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  if (loading && !financeData.trend.length) return <p className="loading-state">Loading dashboard...</p>;

  // =============================================
  // PARENT DASHBOARD RENDER
  // =============================================
  if (isParent) {
    return (
      <div className="dashboard-home parent-view">
        {/* ... existing parent code ... */}
        <div className="dashboard-header parent-header">
          <div className="header-welcome">
            <div className="user-avatar-mini">
              {user?.userName?.charAt(0) || "P"}
            </div>
            <div>
              <h2 className="dashboard-title">Hello, {user?.userName || "Parent"}</h2>
              <p className="subtitle">Your child's progress is looking great today!</p>
            </div>
          </div>
          <div className="quick-actions">
            <button className="qa-btn primary premium" onClick={() => navigate("/dashboard/fees")}>
              <IndianRupee size={16} /> Instant Pay
            </button>
          </div>
        </div>

        <div className="stats-grid">
          <StatCard icon={<Award />} label="Overall Grade" value="Distinction (A+)" tone="blue" />
          <StatCard icon={<GraduationCap />} label="Course Level" value="Level 03" tone="emerald" />
          <StatCard icon={<BookMarked />} label="Pending Work" value="02 Tasks" tone="amber" />
          <StatCard icon={<AlertCircle />} label="Due Fees" value="₹4,500" tone="rose" />
        </div>

        <div className="dashboard-home-content">
          <div className="content-card progress-card">
            <div className="section-header">
              <Activity size={18} />
              <h3>Academic Growth</h3>
            </div>
            <div className="growth-chart-dummy">
              <div className="performance-meter">
                <div className="meter-info"><span>Current Attendance</span><strong>92%</strong></div>
                <div className="meter-bar"><div className="meter-fill success" style={{ width: '92%' }}></div></div>
              </div>
              <div className="performance-meter">
                <div className="meter-info"><span>Assessment Score</span><strong>88%</strong></div>
                <div className="meter-bar"><div className="meter-fill primary" style={{ width: '88%' }}></div></div>
              </div>
            </div>
          </div>

          <div className="content-card insights-card">
            <div className="section-header"><TrendingUp size={18} /><h3>Next Steps</h3></div>
            <div className="next-action-item">
              <div className="action-icon"><Award size={16} /></div>
              <div className="action-info"><strong>Practical Exam</strong><span>Upcoming on 15th May</span></div>
              <ArrowUpRight size={14} className="action-arrow" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =============================================
  // ADMIN DASHBOARD RENDER (FINANCE FOCUSED)
  // =============================================
  const { summary } = financeData;

  return (
    <div className="dashboard-home">

      <DashboardFilters
        userData={user}
        filters={filters}
        onChange={setFilters}
        onApply={applyFilters}
      />

      {/* FINANCE KPI CARDS */}
      <div className="stats-grid">
        <StatCard
          icon={<IndianRupee />}
          label="Total Invoiced"
          value={formatCurrency(summary.TotalAmount)}
          tone="blue"
          subValue={`${summary.TotalInvoices} Invoices`}
        />
        <StatCard
          icon={<ArrowDownCircle />}
          label="Total Collected"
          value={formatCurrency(summary.TotalCollected)}
          tone="emerald"
          subValue="Revenue Received"
        />
        <StatCard
          icon={<Clock />}
          label="Pending Payment"
          value={formatCurrency(summary.TotalPending)}
          tone="amber"
          subValue="In-progress"
        />
        <StatCard
          icon={<AlertCircle />}
          label="Overdue Amount"
          value={formatCurrency(summary.OverdueAmount)}
          tone="rose"
          subValue="Action Required"
        />
      </div>

      <div className="dashboard-grid-layout">
        {/* TREND CHART */}
        <div className="content-card chart-container">
          <div className="section-header">
            <div className="header-title-group">
              <TrendingUp size={18} />
              <h3>Financial Trends</h3>
            </div>
            <div className="chart-legend">
              <div className="legend-item">
                <span className="dot generated"></span> GENERATED
              </div>
              <div className="legend-item">
                <span className="dot collected"></span> COLLECTED
              </div>
            </div>
          </div>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={financeData.trend}>
                <defs>
                  <linearGradient id="colorGen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorCol" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="Month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  tickFormatter={(val) => `₹${val / 1000}k`}
                />
                <Tooltip
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                  formatter={(val) => formatCurrency(val)}
                />
                <Area
                  type="monotone"
                  dataKey="TotalGenerated"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorGen)"
                />
                <Area
                  type="monotone"
                  dataKey="TotalCollected"
                  stroke="#10b981"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorCol)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* RECENT INVOICES */}
        <div className="content-card recent-invoices">
          <div className="section-header">
            <div className="header-title-group">
              <FileText size={18} />
              <h3>Recent Transactions</h3>
            </div>
          </div>
          <div className="invoice-list">
            {financeData.recent.map((inv) => (
              <div className="invoice-item-row" key={inv.InvoiceId}>
                <div className="inv-icon">
                  <div className={`status-dot ${inv.PaymentStatus?.toLowerCase()}`}></div>
                </div>
                <div className="inv-main">
                  <strong>{inv.InvoiceNumber}</strong>
                  <span>Due: {formatDate(inv.DueDate)}</span>
                </div>
                <div className="inv-amount">
                  <strong>{formatCurrency(inv.Amount)}</strong>
                  <span className={`status-tag ${inv.PaymentStatus?.toLowerCase()}`}>{inv.PaymentStatus?.toUpperCase()}</span>
                </div>
              </div>
            ))}
            {financeData.recent.length === 0 && (
              <div className="empty-state">No recent invoices found.</div>
            )}
          </div>
        </div>

        {/* PENDING APPROVALS */}
        <div className="content-card recent-invoices">
          <div className="section-header">
            <div className="header-title-group">
              <Clock size={18} />
              <h3>Pending Approvals</h3>
            </div>
          </div>
          <div className="invoice-list">
            {financeData.pendingApprovals.map((inv, idx) => (
              <div className="invoice-item-row" key={inv.PaymentId || inv.InvoiceId || idx}>
                <div className="inv-icon">
                  <div className="status-dot pending"></div>
                </div>
                <div className="inv-main">
                  <strong>{inv.InvoiceNumber}</strong>
                  <span>{inv.PaidDate ? `Paid: ${formatDate(inv.PaidDate)}` : `Due: ${formatDate(inv.DueDate)}`}</span>
                </div>
                <div className="inv-amount">
                  <strong>{formatCurrency(inv.Amount)}</strong>
                  <span className="status-tag pending">PENDING</span>
                </div>
              </div>
            ))}
            {financeData.pendingApprovals.length === 0 && (
              <div className="empty-state">No pending approvals found.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* MODERN STAT CARD */
const StatCard = ({ icon, label, value, tone, subValue }) => (
  <div className={`stat-card ${tone}`}>
    <div className="stat-icon">{icon}</div>
    <div className="stat-info">
      <span>{label}</span>
      <strong>{value}</strong>
      {subValue && <small>{subValue}</small>}
    </div>
  </div>
);

export default DashboardHome;
